import { authenticatedClient } from '../lib/client';
import { FILES_ENDPOINTS } from './files.utils';
import type { ApiResponse } from '../lib/types';
import type { FileDetails, CreateFilePayload } from './files.types';

export const getAllFiles = async (): Promise<ApiResponse<FileDetails[]>> => {
    try {
        const response = await authenticatedClient.get(FILES_ENDPOINTS.GET_ALL);
        // console.log("Files:", response.data);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to fetch files' };
    }
};

export const getFileById = async (fileId: string): Promise<ApiResponse<FileDetails>> => {
    try {
        const response = await authenticatedClient.get(FILES_ENDPOINTS.GET_BY_ID(fileId));
        // console.log("File:", response.data);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to fetch file' };
    }
};

// Get the correct MIME type for a file (handles Windows browser quirks)
const getCorrectMimeType = (file: File): string => {
    // If the browser provides a valid MIME type, use it
    if (file.type && file.type !== 'application/octet-stream') {
        return file.type;
    }
    
    // Fallback: determine MIME type from file extension
    // This is needed because Windows browsers often report incorrect MIME types
    const extension = file.name.toLowerCase().split('.').pop();
    const mimeTypes: Record<string, string> = {
        'pdf': 'application/pdf',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'txt': 'text/plain',
    };
    
    return mimeTypes[extension || ''] || 'application/octet-stream';
};

export const createFile = async ({ file }: CreateFilePayload): Promise<ApiResponse<FileDetails>> => {
    try {
        const formData = new FormData();
        
        // Create a new File object with the correct MIME type
        // This fixes Windows browsers reporting incorrect MIME types for .md files
        const correctMimeType = getCorrectMimeType(file);
        const correctedFile = new File([file], file.name, { type: correctMimeType });
        
        formData.append('file', correctedFile);

        const response = await authenticatedClient.post(FILES_ENDPOINTS.CREATE, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        // console.log("Created file:", response.data);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to create file' };
    }
};

export const deleteFile = async (fileId: string): Promise<ApiResponse<FileDetails>> => {
    try {
        const response = await authenticatedClient.delete(FILES_ENDPOINTS.DELETE(fileId));
        // console.log("Deleted file:", response.data);
        return { data: response.data };
    } catch (error: any) {
        return { error: error.response?.data?.message || 'Failed to delete file' };
    }
};